import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  PlayIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  ScissorsIcon,
  SparklesIcon,
  FilmIcon,
} from '@heroicons/react/24/outline';
import clipGenerationApi, { IClipGenerationJob, IGeneratedClip } from '@/services/clipGenerationApi';
import LocalizedTextArea from '@/components/Common/LocalizedTextArea';
import YouTubePublishModal from '@/components/Video/YouTubePublishModal';
import SocialPublishModal from '@/components/Video/SocialPublishModal';
import ClipDetailsModal from '@/components/Clips/ClipDetailsModal';
import BulkScheduleModal from '@/components/Clips/BulkScheduleModal';
import { PublishingPlatform } from '@/services/publishingApi';
import { PLATFORMS } from '@/pages/Clips/ClipGeneratorPage';

// Only platforms with a real publishing integration can be bulk-scheduled —
// tiktok/linkedin_video/twitter_video have no backend integration at all.
const BULK_SCHEDULABLE_PLATFORM: Partial<Record<string, PublishingPlatform>> = {
  youtube_shorts: 'youtube',
  instagram_reels: 'instagram',
  facebook_reels: 'facebook',
};

export function StatusBadge({ status }: { status: IClipGenerationJob['status'] }) {
  const colors: Record<string, string> = {
    queued: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    cancelled: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colors[status] || colors.queued}`}>{status}</span>;
}

function groupBy<T, K extends keyof T>(items: T[], key: K): Record<string, T[]> {
  return items.reduce((acc, item) => {
    const groupKey = String(item[key]);
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(item);
    return acc;
  }, {} as Record<string, T[]>);
}

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const MOOD_OPTIONS = [
  'upbeat_energetic',
  'calm_inspirational',
  'corporate_confident',
  'emotional_storytelling',
  'trendy_viral',
];

function Pill({ tone = 'default', children }: { tone?: 'default' | 'amber'; children: React.ReactNode }) {
  const tones: Record<string, string> = {
    default: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${tones[tone]}`}>{children}</span>;
}

function IconButton({
  title,
  onClick,
  disabled,
  danger,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        danger
          ? 'text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

interface ClipDetailsDrawerProps {
  job: IClipGenerationJob;
  onClose: () => void;
  onDeleteClip: (clipId: string) => void;
  onRefreshJob: () => void | Promise<void>;
}

export default function ClipDetailsDrawer({ job, onClose, onDeleteClip, onRefreshJob }: ClipDetailsDrawerProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const groupedClips = useMemo(() => groupBy(job.clips || [], 'platform'), [job.clips]);
  const [publishingClip, setPublishingClip] = useState<IGeneratedClip | null>(null);
  const [socialPublishingClip, setSocialPublishingClip] = useState<IGeneratedClip | null>(null);
  const [editingClipId, setEditingClipId] = useState<string | null>(null);
  const editingClip = editingClipId ? job.clips?.find((c) => c.clipId === editingClipId) || null : null;
  const [retrimClipId, setRetrimClipId] = useState<string | null>(null);
  const [bulkSchedulePlatform, setBulkSchedulePlatform] = useState<{ id: PublishingPlatform; label: string; targetPlatform: string } | null>(null);
  const retrimClip = retrimClipId ? job.clips?.find((c) => c.clipId === retrimClipId) || null : null;

  const handleRetrimSave = async (draft: { start: number; end: number; title: string }) => {
    if (!retrimClip) return;
    try {
      await clipGenerationApi.updateTrim(job.jobId, retrimClip.clipId, draft.start, draft.end, draft.title || undefined);
      toast.success('Clip re-trimmed');
      await onRefreshJob();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to re-trim clip');
      throw err;
    }
  };

  const clipCount = job.clips?.length || 0;
  const isInProgress = (job.status === 'queued' || job.status === 'processing') && clipCount === 0;

  return (
    <>
      <div className="fixed inset-0 z-[60] flex justify-end">
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />
        <div className="relative h-full w-full md:w-[720px] lg:w-[960px] bg-white dark:bg-gray-900 shadow-2xl flex flex-col">
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">Clips</p>
              <h2 className="mt-0.5 text-lg font-semibold text-gray-900 dark:text-white truncate">
                {job.title || 'Untitled'}
              </h2>
              <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>
                  {clipCount} clip{clipCount === 1 ? '' : 's'}
                  {job.status === 'processing' ? ` · ${job.progress}%` : ''}
                </span>
                <StatusBadge status={job.status} />
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-8">
            {job.status === 'failed' && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/20">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">Clip generation failed</p>
                {job.error && <p className="mt-1 text-sm text-red-700 dark:text-red-400">{job.error}</p>}
              </div>
            )}

            {isInProgress && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-8 text-center">
                <ArrowPathIcon className="mx-auto h-6 w-6 animate-spin text-indigo-500" />
                <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-200">{job.currentStep || 'Processing'}…</p>
                <div className="mx-auto mt-3 h-1.5 w-48 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div className="h-full rounded-full bg-indigo-600 transition-all" style={{ width: `${job.progress || 0}%` }} />
                </div>
              </div>
            )}

            {job.highlights && job.highlights.length > 0 && (
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
                  Detected highlights
                </h3>
                <div className="flex flex-wrap gap-2">
                  {job.highlights.slice(0, 12).map((h, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300"
                    >
                      <span className="font-medium text-gray-800 dark:text-gray-100">
                        {formatTime(h.start)}–{formatTime(h.end)}
                      </span>
                      {h.label && <span>· {h.label}</span>}
                      {h.emotion?.primary && (
                        <span
                          title={h.emotion.viralityReason || undefined}
                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            h.emotion.source === 'llm'
                              ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {h.emotion.source === 'llm' ? '✨ ' : ''}
                          {h.emotion.primary.replace(/_/g, ' ')}
                        </span>
                      )}
                      <span className="rounded-full bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 dark:text-indigo-300">
                        {Math.round(h.score * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {clipCount > 0 ? (
              PLATFORMS.filter((p) => groupedClips[p.id]?.length > 0).map((platform) => (
                <section key={platform.id}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{platform.label}</h3>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {groupedClips[platform.id].length} clip{groupedClips[platform.id].length === 1 ? '' : 's'}
                      </span>
                      {groupedClips[platform.id].length > 1 && BULK_SCHEDULABLE_PLATFORM[platform.id] && (
                        <button
                          type="button"
                          onClick={() => setBulkSchedulePlatform({ id: BULK_SCHEDULABLE_PLATFORM[platform.id]!, label: platform.label, targetPlatform: platform.id })}
                          className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                        >
                          Schedule all
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                    {groupedClips[platform.id].map((clip) => (
                      <ClipCard
                        key={clip.clipId}
                        jobId={job.jobId}
                        clip={clip}
                        onDelete={() => onDeleteClip(clip.clipId)}
                        onPublish={() => setPublishingClip(clip)}
                        onSocialPublish={() => setSocialPublishingClip(clip)}
                        onEdit={() => setEditingClipId(clip.clipId)}
                        onRetrim={() => setRetrimClipId(clip.clipId)}
                        canRetrim={!!job.videoS3Url}
                        canPublish={platform.id === 'youtube_shorts'}
                        canSocialPublish={platform.id === 'instagram_reels' || platform.id === 'facebook_reels'}
                        platformLabel={platform.label}
                      />
                    ))}
                  </div>
                </section>
              ))
            ) : !isInProgress && job.status !== 'failed' ? (
              <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-10 text-center">
                <FilmIcon className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No clips generated yet.</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {editingClip && (
        <ClipEditModal
          jobId={job.jobId}
          clip={editingClip}
          onClose={() => setEditingClipId(null)}
          onSaved={onRefreshJob}
        />
      )}
      {retrimClip && job.videoS3Url && (
        <ClipDetailsModal
          videoUrl={job.videoS3Url}
          initialStart={retrimClip.startTime}
          initialEnd={retrimClip.endTime}
          initialTitle={retrimClip.title}
          mode="edit"
          note="Re-trimming re-renders this clip from the original video and clears its existing captions and any mixed-in music — re-apply them afterward if needed."
          onSave={handleRetrimSave}
          onClose={() => setRetrimClipId(null)}
        />
      )}
      {publishingClip && (
        <YouTubePublishModal
          sourceType="clip"
          sourceId={`${job.jobId}:${publishingClip.clipId}`}
          defaultTitle={publishingClip.title || `${job.title || 'AI clip'} #Shorts`}
          defaultDescription={publishingClip.description || ''}
          defaultTags={publishingClip.hashtags || ['Shorts']}
          defaultCaptionsSrtUrl={publishingClip.subtitles?.srtS3Url}
          onClose={() => setPublishingClip(null)}
          onPublished={() => setPublishingClip(null)}
        />
      )}
      {socialPublishingClip && (
        <SocialPublishModal
          sourceId={`${job.jobId}:${socialPublishingClip.clipId}`}
          defaultCaption={socialPublishingClip.description || ''}
          defaultHashtags={socialPublishingClip.hashtags || []}
          onClose={() => setSocialPublishingClip(null)}
          onPublished={() => setSocialPublishingClip(null)}
        />
      )}
      {bulkSchedulePlatform && (
        <BulkScheduleModal
          clipGenerationJobId={job.jobId}
          platform={bulkSchedulePlatform.id}
          platformLabel={bulkSchedulePlatform.label}
          clips={groupedClips[bulkSchedulePlatform.targetPlatform] || []}
          onClose={() => setBulkSchedulePlatform(null)}
          onScheduled={onRefreshJob}
        />
      )}
    </>
  );
}

function ClipCard({
  jobId,
  clip,
  onDelete,
  onPublish,
  onSocialPublish,
  onEdit,
  onRetrim,
  canRetrim,
  canPublish,
  canSocialPublish,
  platformLabel,
}: {
  jobId: string;
  clip: IGeneratedClip;
  onDelete: () => void;
  onPublish: () => void;
  onSocialPublish: () => void;
  onEdit: () => void;
  onRetrim: () => void;
  canRetrim: boolean;
  canPublish: boolean;
  canSocialPublish: boolean;
  platformLabel: string;
}) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const canPublishDirectly = canPublish || canSocialPublish;
  const canPlay = clip.status === 'completed' && !!clip.s3Key;
  const isVertical = clip.aspectRatio === '9:16';

  useEffect(() => {
    if (!clip.thumbnailS3Url) return;
    let active = true;
    clipGenerationApi
      .getThumbnailSignedUrl(jobId, clip.clipId)
      .then((res) => {
        if (active && res.data.success) setThumbnailUrl(res.data.url);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [jobId, clip.clipId, clip.thumbnailS3Url]);

  const startPlayback = async () => {
    if (!canPlay) return;
    if (signedUrl) {
      setPlaying(true);
      return;
    }
    setLoading(true);
    try {
      const res = await clipGenerationApi.getClipSignedUrl(jobId, clip.clipId);
      if (res.data.success) {
        setSignedUrl(res.data.url);
        setPlaying(true);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!canPlay) return;
    setDownloading(true);
    try {
      const res = await clipGenerationApi.getClipSignedUrl(jobId, clip.clipId);
      if (res.data.success) {
        const a = document.createElement('a');
        a.href = res.data.url;
        a.download = `${clip.platform}-${clip.clipId}.mp4`;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to download clip');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden hover:shadow-md transition-shadow">
      <div className={`relative overflow-hidden bg-black ${isVertical ? 'aspect-[9/16]' : 'aspect-video'}`}>
        {playing && signedUrl ? (
          <video key={signedUrl} src={signedUrl} controls autoPlay playsInline className="w-full h-full object-contain" />
        ) : (
          <button
            type="button"
            onClick={startPlayback}
            disabled={!canPlay || loading}
            className="group relative block w-full h-full disabled:cursor-not-allowed"
          >
            {thumbnailUrl && (
              <img
                src={thumbnailUrl}
                alt={clip.thumbnail?.intent ? `${clip.thumbnail.intent} clip thumbnail` : 'Generated clip thumbnail'}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-colors">
              <span className="flex items-center justify-center w-11 h-11 rounded-full bg-white/90 shadow-md group-hover:scale-105 transition-transform">
                {loading ? (
                  <span className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <PlayIcon className="w-5 h-5 text-gray-900 ml-0.5" />
                )}
              </span>
            </span>
            <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
              {clip.length.toFixed(1)}s
            </span>
          </button>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            {formatTime(clip.startTime)}–{formatTime(clip.endTime)}
          </span>
          {typeof clip.engagementScore === 'number' && (
            <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
              <SparklesIcon className="h-3 w-3" />
              {Math.round(clip.engagementScore * 100)}%
            </span>
          )}
        </div>

        {(clip.subtitles || clip.music?.mood || !canPublishDirectly) && (
          <div className="flex flex-wrap gap-1">
            {clip.subtitles && <Pill>{clip.subtitles.burned ? 'Captions burned' : 'Captions ready'}</Pill>}
            {clip.music?.mood && <Pill>{clip.music.mood.replace(/_/g, ' ')}</Pill>}
            {!canPublishDirectly && <Pill tone="amber">Manual upload</Pill>}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-700 pt-2">
          <div className="flex items-center gap-0.5">
            {canPublish && (
              <IconButton title="Publish to YouTube" onClick={onPublish} disabled={!canPlay}>
                <PaperAirplaneIcon className="w-4 h-4" />
              </IconButton>
            )}
            {canSocialPublish && (
              <IconButton title="Publish to Meta" onClick={onSocialPublish} disabled={!canPlay}>
                <PaperAirplaneIcon className="w-4 h-4" />
              </IconButton>
            )}
            <IconButton
              title={canPublishDirectly ? 'Download clip' : `Download to publish manually on ${platformLabel}`}
              onClick={handleDownload}
              disabled={!canPlay || downloading}
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
            </IconButton>
            <IconButton title="Edit captions & music" onClick={onEdit}>
              <PencilSquareIcon className="w-4 h-4" />
            </IconButton>
            <IconButton title="Re-trim clip" onClick={onRetrim} disabled={!canRetrim}>
              <ScissorsIcon className="w-4 h-4" />
            </IconButton>
          </div>
          <IconButton title="Remove clip" onClick={onDelete} danger>
            <TrashIcon className="w-4 h-4" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}

function ClipEditModal({
  jobId,
  clip,
  onClose,
  onSaved,
}: {
  jobId: string;
  clip: IGeneratedClip;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [srtContent, setSrtContent] = useState(clip.subtitles?.srtContent || '');
  const [selectedMood, setSelectedMood] = useState(clip.music?.mood || 'trendy_viral');
  const [musicTrackUrl, setMusicTrackUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSrtContent(clip.subtitles?.srtContent || '');
  }, [clip.subtitles?.srtContent]);

  useEffect(() => {
    setSelectedMood(clip.music?.mood || 'trendy_viral');
  }, [clip.music?.mood]);

  const saveSubtitles = async (burn = false) => {
    setSaving(true);
    try {
      const res = await clipGenerationApi.updateSubtitles(jobId, clip.clipId, srtContent, burn);
      if (res.data.success) {
        toast.success(burn ? 'Subtitles burned into clip' : 'Subtitles updated');
        await onSaved();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update subtitles');
    } finally {
      setSaving(false);
    }
  };

  const saveMusic = async () => {
    setSaving(true);
    try {
      const trimmedUrl = musicTrackUrl.trim();
      const res = await clipGenerationApi.updateMusic(jobId, clip.clipId, selectedMood, undefined, trimmedUrl || undefined);
      if (res.data.success) {
        toast.success(trimmedUrl ? 'Track mixed into clip' : 'Music recommendation updated');
        setMusicTrackUrl('');
        await onSaved();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update music');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadSrt = async () => {
    if (!clip.subtitles?.srtS3Key) return;
    try {
      const res = await clipGenerationApi.getSubtitleSignedUrl(jobId, clip.clipId);
      if (res.data.success) {
        const a = document.createElement('a');
        a.href = res.data.url;
        a.download = `${clip.clipId}.srt`;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to download subtitles');
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Edit clip</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {clip.length.toFixed(1)}s · {formatTime(clip.startTime)}–{formatTime(clip.endTime)}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-5">
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Captions (SRT)</label>
              {clip.subtitles?.srtS3Key && (
                <button
                  type="button"
                  onClick={handleDownloadSrt}
                  className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  Download SRT
                </button>
              )}
            </div>
            <LocalizedTextArea
              value={srtContent}
              onChange={(e) => setSrtContent(e.target.value)}
              rows={8}
              disabled={saving}
              placeholder={'1\n00:00:00,000 --> 00:00:02,000\nYour caption text'}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => saveSubtitles(false)}
                disabled={saving}
                className="rounded-md bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 dark:bg-indigo-900/30 dark:text-indigo-300"
              >
                Save captions
              </button>
              <button
                type="button"
                onClick={() => saveSubtitles(true)}
                disabled={saving}
                className="rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50 dark:bg-blue-900/30 dark:text-blue-300"
              >
                Save &amp; burn in
              </button>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-5 dark:border-gray-700">
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Music mood</label>
            <select
              value={selectedMood}
              onChange={(e) => setSelectedMood(e.target.value)}
              disabled={saving}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            >
              {MOOD_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m.replace(/_/g, ' ')}
                </option>
              ))}
            </select>

            <label className="mb-1.5 mt-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Your own track URL <span className="font-normal text-gray-400">(optional — mixed into the clip's audio)</span>
            </label>
            <input
              type="url"
              value={musicTrackUrl}
              onChange={(e) => setMusicTrackUrl(e.target.value)}
              placeholder="https://your-licensed-track.mp3"
              disabled={saving}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              The mood above is a suggestion only. Paste a link to a track you have rights to use to mix it in.
            </p>
            <button
              type="button"
              onClick={saveMusic}
              disabled={saving}
              className="mt-2 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save music'}
            </button>
          </div>
        </div>

        <div className="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
